import React from 'react';
import { gql, useQuery } from '@apollo/client';
import IdInput from 'components/Filter/IdInput';
import { BooleanField, Datagrid, BooleanInput, DateInput, DateField } from 'react-admin';
import List from 'components/List';
import MarketInput from 'components/MarketInput';
import PositionInput from 'components/PositionInput';
import LinkField from 'components/LinkField';
import BulkUploadButtons from 'components/BulkUploadButtons';
import useGate from '../../../hooks/useGate';
import { gateNames } from '../../../constants/statsig';
import useCheckAccess from 'hooks/useCheckAccess';

const TEMPLATE_URL_QUERY = gql`
  query {
    marketPositionRateSettingTemplateUrl
  }
`;

const MarketPositionRateSettingListFilters = [
  <IdInput />,
  <MarketInput alwaysOn resettable />,
  <PositionInput emptyText="All" alwaysOn />,
  <BooleanInput alwaysOn source="active" />,
  <DateInput alwaysOn label="Last review after" source="search.last_rate_review_from" />,
];

const MarketPositionRateSettingList = () => {
  const checkAccess = useCheckAccess();
  const { value: blockCreateRateSettings } = useGate(gateNames.blockCreateNewRateSettings);
  const canCreateRateSettings =
    checkAccess('actions', 'MarketPositionRateSetting', 'import') && !blockCreateRateSettings;
  const { data: templateUrlData } = useQuery(TEMPLATE_URL_QUERY);
  const downloadUrl = templateUrlData?.marketPositionRateSettingTemplateUrl ?? '';

  const bulkUploadButtons = (
    <BulkUploadButtons
      downloadUrl={downloadUrl}
      uploadUrl="v3/admin/market_position_rate_settings/import"
    />
  );

  return (
    <List
      filters={MarketPositionRateSettingListFilters}
      exporter={false}
      additionalActions={canCreateRateSettings ? bulkUploadButtons : null}
    >
      <Datagrid bulkActionButtons={false}>
        <LinkField label="ID" source="id" />
        <LinkField label="Market" source="market.display_name" sortBy="market_name" />
        <LinkField label="Position" source="position.display_name" sortBy="position_name" />
        <LinkField label="Specialty" source="specialty.label" sortBy="specialty_name" />
        <BooleanField source="active" />
        <DateField source="last_rate_review" />
      </Datagrid>
    </List>
  );
};

MarketPositionRateSettingList.query = gql`
  query marketPositionRateSettings($input: MarketPositionRateSettingQueryInput) {
    _marketPositionRateSettingsMeta(input: $input) {
      count
    }
    marketPositionRateSettings(input: $input) {
      id
      active
      last_rate_review
      market {
        id
        display_name
      }
      position {
        id
        display_name
      }
      specialty {
        id
        label
      }
    }
  }
`;

export default MarketPositionRateSettingList;
